import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { Company } from 'types'
import { v4 as uuid } from 'uuid'

type LogoUpload = {
  file: File | null
  preview_url: string
  isUploaded: boolean
}

type CreateCompanyInput = {
  name: string
  logo: LogoUpload
  owner_avatar?: LogoUpload
  country?: string | null
  email?: string
  provider?: string
  priceId?: string
  // VERA-342: optional Vera subdomain (auto-generated from name when omitted)
  subdomain?: string | null
  // VERA-342: extra company-admin emails invited alongside the primary `email`
  additionalAdminEmails?: string[] | null
  // VERA-342: internal Vera staff (admin ids) linked as managers, no invite
  managerIds?: string[] | null
}

type UpdateCompanyInput = {
  id: string
  name: string
  logo: LogoUpload
  // VRG-18: the optional rest of the logo set. Omit a variant to leave it
  // untouched; pass one with an empty `preview_url` + `isUploaded` to clear it.
  logo_dark?: LogoUpload
  logo_wide?: LogoUpload
  logo_wide_dark?: LogoUpload
  owner_avatar?: LogoUpload
  country: string
  provider?: string
  support_email?: string
  support_phone?: string
  // VRG-246: standing company policy, moved here from the (one-time) create payload
  // so it can actually be changed after the company exists. Passed straight through
  // to `companyData` below — matches the snake_case key the update DTO/use-case use.
  signup_allowed_domain?: string | null
  // VRG-323: company-wide fallback shown in the app's "Where to scan?" panel for any
  // product that sets no instructions of its own. `null` clears the default — the
  // update body spreads `rest` into a JSON blob, and JSON.stringify drops undefined
  // keys, so null is the only value that can actually erase one.
  default_scan_instructions?: string | null
  default_scan_media_url?: string | null
  // VERA-260: analytics reporting currency. Spread straight through to `companyData`
  // below like `signup_allowed_domain` — matches the snake_case key the update
  // DTO/use-case use.
  reporting_currency?: string
}

type DeleteCompanyInput = {
  id: string
}

type UpdateSubdomainInput = {
  companyId: string
  subdomain: string
}

// Settings → Fields, Defaults half. Two fields and no more: the endpoint behind this
// writes only these columns, which is what lets it be gated on `field_default_edit`
// instead of `manage_company` (the wholesale `update` above stays on the latter).
// `null` clears a default; `undefined` leaves it untouched.
type UpdateScanDefaultsInput = {
  companyId: string
  default_scan_instructions?: string | null
  default_scan_media_url?: string | null
}

type SuspendCompanyInput = {
  companyId: string
  reason?: string
}

type UnsuspendCompanyInput = {
  companyId: string
}

type ArchiveCompanyInput = {
  companyId: string
}

type UnarchiveCompanyInput = {
  companyId: string
}

type CancelSubscriptionInput = {
  companyId: string
  reason: string
  feedback?: string
}

type CancelSubscriptionResponse = {
  subscription: any
  cancels_at: Date | null
}

type CompSubscriptionInput = {
  companyId: string
  // VRG-247 revision: Stripe coupons only support whole-month durations
  // (`duration_in_months`) — 1-24, not just the two quick-pick presets.
  durationMonths: number
  reason: string
}

type CompSubscriptionResponse = {
  comp: any
}

type GrantActivationsInput = {
  companyId: string
  amount: number
  reason: string
}

type GrantActivationsResponse = {
  companyId: string
  amount: number
  newBalance: number
}

type SuspendMemberInput = {
  companyId: string
  userId: string
  reason?: string
}

type UnsuspendMemberInput = {
  companyId: string
  userId: string
}

type UseCompanyMutationsOptions = {
  onSuccess?: (data?: any) => void
  onError?: (error?: any) => void
}

type UseCompanyMutationsResult = {
  create: (payload: CreateCompanyInput) => void
  update: (payload: UpdateCompanyInput) => void
  remove: (payload: DeleteCompanyInput) => void
  updateSubdomain: (payload: UpdateSubdomainInput) => Promise<Company>
  updateScanDefaults: (payload: UpdateScanDefaultsInput) => void
  suspend: (payload: SuspendCompanyInput) => void
  unsuspend: (payload: UnsuspendCompanyInput) => void
  archive: (payload: ArchiveCompanyInput) => void
  unarchive: (payload: UnarchiveCompanyInput) => void
  suspendMember: (payload: SuspendMemberInput) => void
  unsuspendMember: (payload: UnsuspendMemberInput) => void
  cancelSubscription: (payload: CancelSubscriptionInput) => void
  isCancellingSubscription: boolean
  compSubscription: (payload: CompSubscriptionInput) => void
  isCompingSubscription: boolean
  grantActivations: (payload: GrantActivationsInput) => void
  isGrantingActivations: boolean
  isLoading: boolean
  isError: boolean
}

export const useCompanyMutationsQuery = (options?: UseCompanyMutationsOptions): UseCompanyMutationsResult => {
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: async (payload: CreateCompanyInput) => {
      const { logo, owner_avatar, email, priceId, provider, ...companyData } = payload

      // Create FormData for multipart/form-data request
      const formData = new FormData()

      // Generate company ID if not exists
      const companyId = companyData.name ? uuid() : ''

      // Add company data as stringified JSON
      formData.append('data', JSON.stringify({
        ...companyData,
        id: companyId,
        ...(provider && { provider }),
      }))

      // Add optional fields
      if (email) {
        formData.append('email', email)
      }
      formData.append('priceId', priceId || '') // Required by API but can be empty

      // Add logo file if exists
      if (logo?.file) {
        formData.append('file', logo.file)
      }

      // Add owner avatar file if exists
      if (owner_avatar?.file) {
        formData.append('owner_avatar_file', owner_avatar.file)
      }

      const response = await createApiCall({
        method: 'POST',
        url: '/companies/admin',
        data: formData,
      })

      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateCompanyInput) => {
      const { id, logo, logo_dark, logo_wide, logo_wide_dark, owner_avatar, ...rest } = payload

      // Create FormData for multipart/form-data request
      const formData = new FormData()

      // Add company data as stringified JSON
      const companyData: any = { ...rest }

      // If logo is already uploaded, include its path
      if (logo.isUploaded && logo.preview_url) {
        companyData.logo = logo.preview_url
      }

      // VRG-18: the three optional variants. Each gets its own JSON key + its
      // own file part. A variant left `undefined` is absent from BOTH, which
      // the API reads as "not part of this update" — so saving the company name
      // never wipes the logo set. An uploaded-but-empty `preview_url` IS sent,
      // as an empty string, because that is how "Remove" clears a variant.
      const variants = [
        { field: 'logo_dark_file', key: 'logo_dark', value: logo_dark },
        { field: 'logo_wide_file', key: 'logo_wide', value: logo_wide },
        { field: 'logo_wide_dark_file', key: 'logo_wide_dark', value: logo_wide_dark },
      ] as const

      // NOTE: this loop MUST run before `data` is serialized below — the
      // clear-a-variant case writes into `companyData`.
      for (const { key, value } of variants) {
        if (!value) continue
        if (value.isUploaded) companyData[key] = value.preview_url
        // Not uploaded and no file = the Remove control fired. Send the empty
        // string so the API clears the column rather than skipping the variant.
        else if (!value.file) companyData[key] = ''
      }

      // If owner avatar is already uploaded, include its path
      if (owner_avatar?.isUploaded && owner_avatar?.preview_url) {
        companyData.owner_avatar = owner_avatar.preview_url
      }

      formData.append('data', JSON.stringify(companyData))

      // Add logo file if it's a new upload
      if (!logo.isUploaded && logo.file) {
        formData.append('file', logo.file)
      }

      for (const { field, value } of variants) {
        if (value && !value.isUploaded && value.file) formData.append(field, value.file)
      }

      // Add owner avatar file if it's a new upload
      if (owner_avatar && !owner_avatar.isUploaded && owner_avatar.file) {
        formData.append('owner_avatar_file', owner_avatar.file)
      }

      const response = await createApiCall({
        method: 'PUT',
        url: `/companies/admin/${id}`,
        data: formData,
      })

      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', data.id] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (payload: DeleteCompanyInput) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `/companies/admin/${payload.id}`,
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const updateSubdomainMutation = useMutation({
    mutationFn: async (payload: UpdateSubdomainInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/companies/admin/${payload.companyId}/subdomain`,
        data: { subdomain: payload.subdomain },
      })
      // The axios interceptor RESOLVES (does not throw) on error responses, so
      // a 400 (e.g. handle already taken / reserved) would otherwise be treated
      // as success. Surface it as an error so callers can show the message.
      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          response.data?.message || 'Failed to update company handle',
        )
      }
      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', data.id] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'current'] })
      queryClient.invalidateQueries({ queryKey: ['installations'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const updateScanDefaultsMutation = useMutation({
    mutationFn: async ({ companyId, ...body }: UpdateScanDefaultsInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/companies/admin/${companyId}/scan-defaults`,
        data: body,
      })
      // Same reason as updateSubdomain above: the axios interceptor RESOLVES on error
      // responses rather than throwing, so a 403 from the permission guard would
      // otherwise render as a success toast over an unsaved form.
      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          response.data?.message || 'Failed to save the default scan instructions',
        )
      }
      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'current'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', data.id] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const suspendMutation = useMutation({
    mutationFn: async (payload: SuspendCompanyInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/internal/companies/${payload.companyId}/suspend`,
        data: { reason: payload.reason },
      })
      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', data.id] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const unsuspendMutation = useMutation({
    mutationFn: async (payload: UnsuspendCompanyInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/internal/companies/${payload.companyId}/unsuspend`,
      })
      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', data.id] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const archiveMutation = useMutation({
    mutationFn: async (payload: ArchiveCompanyInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/internal/companies/${payload.companyId}/archive`,
      })
      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', data.id] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const unarchiveMutation = useMutation({
    mutationFn: async (payload: UnarchiveCompanyInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/internal/companies/${payload.companyId}/unarchive`,
      })
      return response.data as Company
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail', data.id] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const suspendMemberMutation = useMutation({
    mutationFn: async (payload: SuspendMemberInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/internal/companies/${payload.companyId}/members/${payload.userId}/suspend`,
        data: { reason: payload.reason },
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail'] })
      queryClient.invalidateQueries({ queryKey: ['company', 'users'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const unsuspendMemberMutation = useMutation({
    mutationFn: async (payload: UnsuspendMemberInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/internal/companies/${payload.companyId}/members/${payload.userId}/unsuspend`,
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail'] })
      queryClient.invalidateQueries({ queryKey: ['company', 'users'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (payload: CancelSubscriptionInput) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/internal/companies/${payload.companyId}/subscription/cancel`,
        data: { reason: payload.reason, feedback: payload.feedback },
      })
      return response.data as CancelSubscriptionResponse
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const compSubscriptionMutation = useMutation({
    mutationFn: async (payload: CompSubscriptionInput) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/internal/companies/${payload.companyId}/subscription/comp`,
        data: { durationMonths: payload.durationMonths, reason: payload.reason },
      })
      return response.data as CompSubscriptionResponse
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const grantActivationsMutation = useMutation({
    mutationFn: async (payload: GrantActivationsInput) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/internal/companies/${payload.companyId}/activations/grant`,
        data: { amount: payload.amount, reason: payload.reason },
      })
      return response.data as GrantActivationsResponse
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['companies', 'detail'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || updateSubdomainMutation.isPending || updateScanDefaultsMutation.isPending || suspendMutation.isPending || unsuspendMutation.isPending || archiveMutation.isPending || unarchiveMutation.isPending
  const isError = createMutation.isError || updateMutation.isError || deleteMutation.isError || updateSubdomainMutation.isError || updateScanDefaultsMutation.isError || suspendMutation.isError || unsuspendMutation.isError || archiveMutation.isError || unarchiveMutation.isError

  return {
    create: createMutation.mutate,
    update: updateMutation.mutate,
    remove: deleteMutation.mutate,
    updateSubdomain: updateSubdomainMutation.mutateAsync,
    updateScanDefaults: updateScanDefaultsMutation.mutate,
    suspend: suspendMutation.mutate,
    unsuspend: unsuspendMutation.mutate,
    archive: archiveMutation.mutate,
    unarchive: unarchiveMutation.mutate,
    suspendMember: suspendMemberMutation.mutate,
    unsuspendMember: unsuspendMemberMutation.mutate,
    cancelSubscription: cancelSubscriptionMutation.mutate,
    isCancellingSubscription: cancelSubscriptionMutation.isPending,
    compSubscription: compSubscriptionMutation.mutate,
    isCompingSubscription: compSubscriptionMutation.isPending,
    grantActivations: grantActivationsMutation.mutate,
    isGrantingActivations: grantActivationsMutation.isPending,
    isLoading,
    isError,
  }
}
